/* ==================== */
/* スタッフ設定画面 */
/* ==================== */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import BottomSheet from '../../components/BottomSheet/BottomSheet';
import FilterButton from '../../components/FilterButton/FilterButton';
import Loading from '../../components/Loading/Loading';
import Modal from '../../components/Modal/Modal';
import Pagination from '../../components/Pagination/Pagination';
import SortingButton from '../../components/SortingButton/SortingButton';
import StaffGroupForm from '../../components/Form/StaffGroupForm/StaffGroupForm';
import useWindowSize from '../../hook/useWindowSize';
import { GROUP_STATUS, RESPONSE, STAFF_STATUS, STATUS_TYPE } from '../../const/Enum';
import { Message } from '../../const/Constant';
import { requestApiLoad, requestApiLoadAndBottom } from '../../utils/apiLoadUtil';
import { getCurrentDateString } from '../../utils/dateUtils';
import * as Validators from '../../utils/validation';
import './StaffGroupSetting.css';

const StaffGroupSetting = () => {
  const [itemList, setItemList] = useState([]);
  const [addItem, setAddItem] = useState({});
  const [editItem, setEditItem] = useState({});
  const [settingItem, setSettingItem] = useState({ userList: [] });
  const [isInit, setIsInit] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});
  const bottomSheetRef = useRef(null);
  const size = useWindowSize();
  const today = getCurrentDateString();

  /* メイン一覧画面 */
  const [mainSearchTerm, setMainSearchTerm] = useState('');
  const [mainStatusFilter, setMainStatusFilter] = useState(0);
  const [mainSortConfig, setMainSortConfig] = useState({ key: 'groupId', direction: true });
  const [mainCurrentPage, setMainCurrentPage] = useState(1);
  const [mainLastPage, setMainLastPage] = useState(1);
  const [mainTotalCount, setMainTotalCount] = useState(0);
  const mainStatusOptions = [
    { value: GROUP_STATUS.ALL, label: 'すべて' },
    { value: GROUP_STATUS.AFFILIATION, label: '所属中' },
    { value: GROUP_STATUS.NOT_AFFILIATION, label: '未所属' },
  ];
  const mainSortOptions = [
    { key: 'groupId', value: { key: 'groupId', direction: true }, label: '標準' },
    { key: 'groupName_asc', value: { key: 'groupName', direction: false }, label: 'グループ名（昇順）' },
    { key: 'groupName_desc', value: { key: 'groupName', direction: true }, label: 'グループ名（降順）' },
  ];

  /* サブ一覧画面 */
  const [subSearchTerm, setSubSearchTerm] = useState('');
  const [subStatusFilter, setSubStatusFilter] = useState(0);
  const [subSortConfig, setSubSortConfig] = useState({ key: 'staffId', direction: true });
  const [subCurrentPage, setSubCurrentPage] = useState(1);
  const [subLastPage, setSubLastPage] = useState(1);
  const [subTotalCount, setSubTotalCount] = useState(0);
  const subStatusOptions = [
    { value: STAFF_STATUS.ALL, label: 'すべて' },
    { value: STAFF_STATUS.ACTIVE, label: '利用中' },
    { value: STAFF_STATUS.SCHEDULED, label: '準備中' },
    { value: STAFF_STATUS.INACTIVE, label: '停止中' }
  ];
  const subSortOptions = [
    { key: 'staffId', value: { key: 'staffId', direction: true }, label: '標準' },
    { key: 'staffName_asc', value: { key: 'staffName', direction: false }, label: 'スタッフ名（昇順）' },
    { key: 'staffName_desc', value: { key: 'staffName', direction: true }, label: 'スタッフ名（降順）' },
    { key: 'startDate_asc', value: { key: 'startDate', direction: false }, label: '開始日（昇順）' },
    { key: 'startDate_desc', value: { key: 'startDate', direction: true }, label: '開始日（降順）' }
  ];
  
    /* 画面状態 */
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showSetting, setShowSetting] = useState(false);

  // ------------------------------------------------------------------------------------
  // 初期処理
  // ------------------------------------------------------------------------------------
  useEffect(() => {
    const fetchData = async () => {
      const params = {
        page: 1,
        isDesc: mainSortConfig.direction,
        orderKey: mainSortConfig.key,
        status: mainStatusFilter,
        name: mainSearchTerm,
      }
      const res = await requestApiLoad('/staffGroup/get', params, setIsLoading);
      if (res.return !== RESPONSE.SUCCESS) {
        return;
      }
      
      setSelectedItem(res.groupList && res.groupList.length > 0 ? res.groupList[0] : {});
      setItemList(res.groupList && res.groupList.length > 0 ? res.groupList : []);
      setMainCurrentPage(1);
      setMainLastPage(res.lastPage);
      setMainTotalCount(res.dataCount);
    };
    fetchData();
  }, [mainStatusFilter, mainSortConfig.direction, mainSortConfig.key, mainSearchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      const params = {
        page: 1,
        isDesc: subSortConfig.direction,
        orderKey: subSortConfig.key,
        status: subStatusFilter,
        name: subSearchTerm,
        groupId: selectedItem.groupId,
      }
      
      const res = await requestApiLoad(`/staff/get`, params, setIsLoading);
      if (res.return !== RESPONSE.SUCCESS) {
        return;
      }
  
      setSettingItem(res.userList);
      setSubCurrentPage(1);
      setSubLastPage(res.lastPage);
      setSubTotalCount(res.dataCount);
    };
    fetchData();
    // eslint-disable-next-line
  }, [subStatusFilter, subSortConfig.direction, subSortConfig.key, subSearchTerm]);

  useEffect(() => {
    if (!isInit) { return; }
    if (itemList.length > 0) {
      if (size.width > 1200) {
        handleEdit(null, itemList[0]);
        changeScreenMode(MODE.EDIT);
      } else {
        changeScreenMode(MODE.NONE);
      }
      setIsInit(false);
    }
    // eslint-disable-next-line
  }, [itemList]);

  useEffect(() => {
    if (!selectedItem) {
      setSelectedItem({});
      return;
    }
    createEditTable();
    // eslint-disable-next-line
  }, [selectedItem]);

  // ------------------------------------------------------------------------------------
  // 画面モード
  // ------------------------------------------------------------------------------------
  const MODE = {
    NONE: 'NONE',
    ADD: 'ADD',
    EDIT: 'EDIT',
    SETTING: 'SETTING',
  }

  const changeScreenMode = (mode) => {
    switch (mode) {
      case MODE.ADD:
        setShowAdd(true);
        setShowEdit(false);
        setShowSetting(false);
        break;
      
      case MODE.EDIT:
        setShowAdd(false);
        setShowEdit(true);
        setShowSetting(false);
        break;
      
      case MODE.SETTING:
        setShowAdd(false);
        setShowEdit(false);
        setShowSetting(true);
        break;
      
      case MODE.NONE:
      default:
        setShowAdd(false);
        setShowEdit(false);
        setShowSetting(false);
        break;
    }
  }

  // ------------------------------------------------------------------------------------
  // ページネーション
  // ------------------------------------------------------------------------------------
  const handleMainPageChange = async (pageNumber) => {
    setMainCurrentPage(pageNumber);
    
    const params = {
      page: pageNumber,
      isDesc: mainSortConfig.direction,
      orderKey: mainSortConfig.key,
      status: mainStatusFilter,
      name: mainSearchTerm,
    }
    
    const res = await requestApiLoad('/staffGroup/get', params, setIsLoading);
    if (res.return !== RESPONSE.SUCCESS) {
      return;
    }

    setSelectedItem(res.groupList[0]);
    setItemList(res.groupList);
    setMainLastPage(res.lastPage);
    setMainTotalCount(res.dataCount);
  }
  
  const handleSubPageChange = async (pageNumber) => {
    setSubCurrentPage(pageNumber);
    
    const params = {
      page: pageNumber,
      isDesc: subSortConfig.direction,
      orderKey: subSortConfig.key,
      status: subStatusFilter,
      name: subSearchTerm,
      groupId: selectedItem.groupId,
    }
    
    const res = await requestApiLoad('/staff/get', params, setIsLoading);
    if (res.return !== RESPONSE.SUCCESS) {
      return;
    }

    setSettingItem(res.userList);
    setSubLastPage(res.lastPage);
    setSubTotalCount(res.dataCount);
  }

  // ------------------------------------------------------------------------------------
  // フィルター
  // ------------------------------------------------------------------------------------
  const handleMainStatusChange = async (event) => {
    const newStatusFilter = Number(event.target.value);
    setMainCurrentPage(1);
    setMainStatusFilter(newStatusFilter);
  }

  const handleSubStatusChange = async (event) => {
    const newStatusFilter = Number(event.target.value);
    setSubCurrentPage(1);
    setSubStatusFilter(newStatusFilter);
  }

  const sortedAndFilteredItems = useMemo(() => {
    let filteredItems = itemList || [];
    return filteredItems;
  }, [itemList]);

  // ------------------------------------------------------------------------------------
  // ソート
  // ------------------------------------------------------------------------------------
  const handleMainSortChange = async (event) => {
    const newSortConfig = !event ? { key: 'groupId', direction: true } : JSON.parse(event);
    setMainSortConfig(newSortConfig);
  }

  const handleSubSortChange = async (event) => {
    const newSortConfig = !event ? { key: 'staffId', direction: true } : JSON.parse(event);
    setSubSortConfig(newSortConfig);
  }

  // ------------------------------------------------------------------------------------
  // 検索
  // ------------------------------------------------------------------------------------
  const onMainClear = () => {
    setMainSearchTerm('');
  }

  const onSubClear = () => {
    setSubSearchTerm('');
  }

  // ------------------------------------------------------------------------------------
  // 新規追加
  // ------------------------------------------------------------------------------------
  const handleAdd = async () => {
    setAddItem({
      groupId: '',
      groupName: '',
      groupNameKana: '',
    });
    changeScreenMode(MODE.ADD);
  };

  const handleAddCancel = async (event) => {
    event.preventDefault();
    
    setItemList(itemList);
    setMainLastPage(mainLastPage);
    setMainTotalCount(mainTotalCount);
    setSelectedItem(itemList[0]);

    if(!itemList || itemList?.length === 0) {
      changeScreenMode(MODE.NONE);
    } else if (size.width <= 1200) {
      changeScreenMode(MODE.NONE);
    } else {
      changeScreenMode(MODE.EDIT);
    }
  }

  const handleAddSubmit = async (event) => {
    event.preventDefault();
    const params = {
      groupName: addItem.groupName,
      groupNameKana: addItem.groupNameKana,
    }

    const validationResult = validateCheck(params);
    if (validationResult.error) {
      alert(validationResult.message);
      return;
    }

    const res = await requestApiLoadAndBottom('/staffGroup/add',
      params, setIsLoading, bottomSheetRef, Message.BS_STAFF_GROUP_ADD_SUCCESS);
    if (res.return !== RESPONSE.SUCCESS) {
      return;
    }
    setFirstItem(res);

    if (size.width <= 1200) {
      changeScreenMode(MODE.NONE);
    } else {
      changeScreenMode(MODE.EDIT);
    } 
  }

  // ------------------------------------------------------------------------------------
  // 編集
  // ------------------------------------------------------------------------------------
  const handleEdit = async (e, item) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    if (!item) { return; }
    setSelectedItem(item);
    setEditItem(
      {
        groupId: item.groupId,
        groupName: item.groupName,
        groupNameKana: item.groupNameKana,
      }
    )
    changeScreenMode(MODE.EDIT);
  }

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    const params = {
      groupId: selectedItem.groupId,
      groupName: selectedItem.groupName,
      groupNameKana: selectedItem.groupNameKana,
    }

    const res = await requestApiLoadAndBottom(`/staffGroup/update/${selectedItem.groupId}`,
      params, setIsLoading, bottomSheetRef, Message.BS_STAFF_GROUP_UPDATE_SUCCESS);
    if (res.return !== RESPONSE.SUCCESS) {
      return;
    }
    setFirstItem(res);
      
    if (size.width <= 1200) {
      changeScreenMode(MODE.NONE);
    } else {
      changeScreenMode(MODE.EDIT);
    } 
  }

  const handleDeleteSubmit = async (event) => {
    event.preventDefault();
    if (!window.confirm(`「${selectedItem.groupName}」を削除してもよろしいですか？\n\n※ 所属スタッフがいる場合は自動的に「未所属」に変更されます。`)) { return; }

    const res = await requestApiLoadAndBottom(`/staffGroup/delete/${selectedItem.groupId}`,
      {}, setIsLoading, bottomSheetRef, Message.BS_STAFF_GROUP_DELETE_SUCCESS);
    if (res.return !== RESPONSE.SUCCESS) {
      return;
    }
    setFirstItem(res);
    
    if (size.width <= 1200) {
      changeScreenMode(MODE.NONE);
    } else {
      changeScreenMode(MODE.EDIT);
    }
  }

  // ------------------------------------------------------------------------------------
  // 設定
  // ------------------------------------------------------------------------------------
  const handleSetting = async (e, item) => {
    e.stopPropagation();
    if (!item) { return; }
    setSelectedItem(item);
    
    const params = {
      page: 1,
      isDesc: subSortConfig.direction,
      orderKey: subSortConfig.key,
      status: subStatusFilter,
      name: subSearchTerm,
      groupId: item.groupId,
    }
    
    const res = await requestApiLoad(`/staff/get`, params, setIsLoading);
    if (res.return !== RESPONSE.SUCCESS) {
      return;
    }

    setSettingItem(res.userList);    
    changeScreenMode(MODE.SETTING);
    setSubCurrentPage(1);
    setSubLastPage(res.lastPage);
    setSubTotalCount(res.dataCount);
  }

  // ------------------------------------------------------------------------------------
  // その他
  // ------------------------------------------------------------------------------------ 
  const getStatusText = (item) => {
    switch (item.status) {
      case STAFF_STATUS.ACTIVE :
        return ' (利用中)';
      
      case STAFF_STATUS.SCHEDULED:
        return ' (準備中)';
      
      default:
        return ' (停止中)';
    }
  }

  const setFirstItem = (res) => {
    setItemList(res.groupList);
    setMainLastPage(res.lastPage);
    setMainTotalCount(res.dataCount);
    setSelectedItem(res.groupList[0]);
    setEditItem(res.groupList[0]);
    
    setMainCurrentPage(1);
    setMainStatusFilter(Number(STAFF_STATUS.ALL));
    setMainSearchTerm('');
    setMainSortConfig({ key: 'groupId', direction: true });
    resetScroll();
    resetScrollMain();
  }

  const resetScroll = () => {
    const scrollForm = document.querySelector('.scroll-form');
    if (scrollForm) {
      scrollForm.scrollTop = 0;
    }
  }

  const resetScrollMain = () => {
    const scrollForm = document.querySelector('.card-list');
    if (scrollForm) {
      scrollForm.scrollTop = 0;
    }
  }

  // ------------------------------------------------------------------------------------
  // ValidationCheck
  // ------------------------------------------------------------------------------------
  const validateCheck = (params) => {
    // グループ名
    const groupNameResult = Validators.validateGroupName(params.groupName);
    if (groupNameResult.error) {
      return { message: groupNameResult.message, error: true };
    }
    // グループ名（かな）
    const groupNameKanaResult = Validators.validateGroupNameKana(params.groupNameKana);
    if (groupNameKanaResult.error) {
      return { message: groupNameKanaResult.message, error: true };
    }
    return { message: '', error: false };
  }


  // ------------------------------------------------------------------------------------
  // 一覧作成
  // ------------------------------------------------------------------------------------ 
  const createTable = (itemList) => {
    return (
      <div className="card-list">
        {itemList.length === 0 ? (
          <div className="no-data">データがありません</div>
        ) : (
          itemList.map((item) => (
            <div className="card" key={item.groupId} onClick={(e) => handleEdit(e, item)}>
              <div className="card-contents" id="staff-group-setting-card-contents">
                <div className="card-contents-left">
                  <h3>{item.groupName}</h3>
                  {item.parsonCount > 0 ? (
                    <span className="sub-text">所属スタッフ： {item.parsonCount} 人</span>
                  ) : (
                    <span className="sub-text">所属スタッフ： -</span>
                  )}
                </div>
                <div className="card-footer">
                  {/* 設定 */}
                  <button className="blue" onClick={(e) => handleSetting(e, item)} title="設定">
                    <svg
                      width="20px"
                      height="20px"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-labelledby="pollIconTitle"
                      stroke="var(--blue-font-color)"
                      strokeWidth="2.0"
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      color="var(--blue-font-color)">
                      <title id="pollIconTitle">設定</title>
                      <path d="M4 4V20" />
                      <path d="M4 6H15V10H4" />
                      <path d="M4 10H19V14H4" />
                      <path d="M4 14H12V18H4" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    )
  }

  // ------------------------------------------------------------------------------------
  // AddTable
  // ------------------------------------------------------------------------------------
  const createAddTable = () => {
    return (
      <>
        <div className="scroll-form">
          <StaffGroupForm
            formData={addItem}
            setFormData={setAddItem}
          />
        </div>
        <div className="bottom-button">
          <button onClick={handleAddCancel}>
            キャンセル
          </button>
          <button className="blue" onClick={handleAddSubmit}>
            登録
          </button>
        </div>
      </>
    )
  }

  // ------------------------------------------------------------------------------------
  // EditTable
  // ------------------------------------------------------------------------------------
  const createEditTable = () => {
    if (!selectedItem?.groupId) {
      return (
        <div className="no-data">グループが選択されていません</div>
      );
    }
    
    return (
      <>
        <div className="scroll-form">
          <StaffGroupForm
            formData={selectedItem}
            setFormData={setSelectedItem}
          />
        </div>
        <div className="bottom-button">
          <button className="red" onClick={handleDeleteSubmit}>
            削除
          </button>
          <button className="blue" onClick={handleEditSubmit}>
            更新
          </button>
        </div>
      </>
    )
  }

  // ------------------------------------------------------------------------------------
  // SettingTable
  // ------------------------------------------------------------------------------------
  const createSettingTable = () => {
    return (
      <div className="card-list">
        {!settingItem || settingItem.length === 0 || !Array.isArray(settingItem) ? (
          <div className="no-data">データがありません</div>
        ) : (
          settingItem && settingItem.map((staff) => (
              <div className="card" key={staff.staffId}>
              <div className="card-contents">
                <div className="card-contents-left">
                  <h3>{staff.staffName}</h3>
                  <span className="sub-text">{staff.startDate} ~ {staff.endDate}
                    {getStatusText(staff)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    )
  }

  // ------------------------------------------------------------------------------------
  // レンダリング
  // ------------------------------------------------------------------------------------
  return (
    <div className="view-contents" id="staff-group-setting">
      <Loading isLoading={isLoading} />
      <BottomSheet ref={bottomSheetRef} />

      <div className="main-contents">
        {size.width > 1200 && (<h2 className="page-title">グループ設定</h2>)}
        <div className="header-contents">
          <div className="search-bar">
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                placeholder='グループ名で検索'
                value={mainSearchTerm || ''}
                onChange={(e) => setMainSearchTerm(e.target.value)}
                maxLength={10}
                style={{
                  backgroundImage: 'url(/images/search.png)',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: '10px center',
                  backgroundSize: '20px 20px',
                  paddingLeft: '40px',
                  paddingRight: mainSearchTerm ? '30px' : '10px'
                }}
              />
              {mainSearchTerm && (
                <button
                  onClick={onMainClear}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#878787'
                  }}>
                  ×
                </button>
              )}
            </div>
          </div>
          <div className="button-container">
            <div className="add-new-item-button" onClick={handleAdd}>
              <svg
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                width="18px"
                height="18px"
                viewBox="0 0 24 24"
                aria-labelledby="plusIconTitle"
                stroke="var(--blue-font-color)"
                strokeWidth="1.8"
                strokeLinecap="square"
                strokeLinejoin="miter"
                fill="none"
                color="var(--blue-font-color)"
              >
                <title id="plusIconTitle">新規追加</title>
                <path d="M20 12L4 12M12 4L12 20" />
              </svg>
              <p className="hide-option">新規追加</p>
            </div>
          </div>
        </div>

        <div className="middle-contents">
          <div className="left-contents">
            <FilterButton
              statusFilter={mainStatusFilter}
              handleStatusChange={handleMainStatusChange}
              statusOptions={mainStatusOptions}
            />
            <SortingButton
              sortConfig={mainSortConfig}
              handleSortChange={handleMainSortChange}
              sortOptions={mainSortOptions}
            />
          </div>
          <Pagination
            totalPages={mainLastPage}
            currentPage={mainCurrentPage}
            onPageChange={handleMainPageChange}
            totalItems={mainTotalCount}
          />
        </div>

        {createTable(sortedAndFilteredItems)}
      </div>

      {size.width > 1200 ? (
        <>
          {showAdd && (
            <div className="sub-contents">
              <h2 className="page-title">グループ追加</h2>
              {createAddTable()}
            </div>
          )}

          {showEdit && selectedItem?.groupName && (
            <div className="sub-contents">
              <h2 className="page-title">{selectedItem?.groupName}</h2>
              {createEditTable()}
            </div>
          )}

          {showSetting && (
            <div className="sub-contents">
              <h2 className="page-title">{selectedItem.groupName}</h2>
              <div className="header-contents">
                <div className="search-bar">
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="スタッフ名で検索"
                      value={subSearchTerm}
                      onChange={(e) => setSubSearchTerm(e.target.value)}
                      maxLength={10}
                      style={{
                        backgroundImage: 'url(/images/search.png)',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: '10px center',
                        backgroundSize: '20px 20px',
                        paddingLeft: '40px',
                        paddingRight: subSearchTerm ? '30px' : '10px'
                      }}
                    />
                    {subSearchTerm && (
                      <button
                        onClick={onSubClear}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '16px',
                          color: '#878787'
                        }}>
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="middle-contents">
                <div className="left-contents">
                  <FilterButton
                    statusFilter={subStatusFilter}
                    handleStatusChange={handleSubStatusChange}
                    statusOptions={subStatusOptions}
                  />
                  <SortingButton
                    sortConfig={subSortConfig}
                    handleSortChange={handleSubSortChange}
                    sortOptions={subSortOptions}
                  />
                </div>
                <Pagination
                  totalPages={subLastPage}
                  currentPage={subCurrentPage}
                  onPageChange={handleSubPageChange}
                  totalItems={subTotalCount}
                />
              </div>

              {createSettingTable(settingItem)}
            </div>
          )}
        </>
      ) : (
        <>
          <Modal
            isOpen={showAdd}
            title="グループ追加"
            onAction={handleAddSubmit}
            actionButtonText="登録"
            closeModal={() => setShowAdd(false)}
            closeButtonText="閉じる"
          >
            <div className="sub-contents">
              {createAddTable()}
            </div>
          </Modal>
            
          <Modal
            isOpen={showEdit && selectedItem?.groupName}
            title={selectedItem?.groupName || ''}
            closeModal={() => setShowEdit(false)}
            closeButtonText="閉じる"
            actionButtonText="更新"
            onAction={handleEditSubmit}
            actionSubButtonText="削除"
            onSubAction={handleDeleteSubmit}
          >
            <div className="sub-contents">
              {createEditTable()}
            </div>
          </Modal>
            
          <Modal
            isOpen={showSetting && selectedItem?.groupName}
            title={selectedItem?.groupName || ''}
            closeModal={() => setShowSetting(false)}
            closeButtonText="閉じる"
          >
            <div className="sub-contents">
              <div className="header-contents">
                <div className="search-bar">
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="スタッフ名で検索"
                      value={subSearchTerm}
                      onChange={(e) => setSubSearchTerm(e.target.value)}
                      maxLength={10}
                      style={{
                        backgroundImage: 'url(/images/search.png)',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: '10px center',
                        backgroundSize: '20px 20px',
                        paddingLeft: '40px',
                        paddingRight: subSearchTerm ? '30px' : '10px'
                      }}
                    />
                    {subSearchTerm && (
                      <button
                        onClick={onSubClear}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '16px',
                          color: '#878787'
                        }}>
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="middle-contents">
                <div className="left-contents">
                  <FilterButton
                    statusFilter={subStatusFilter}
                    handleStatusChange={handleSubStatusChange}
                    statusOptions={subStatusOptions}
                  />
                  <SortingButton
                    sortConfig={subSortConfig}
                    handleSortChange={handleSubSortChange}
                    sortOptions={subSortOptions}
                  />
                </div>
                <Pagination
                  totalPages={subLastPage}
                  currentPage={subCurrentPage}
                  onPageChange={handleSubPageChange}
                  totalItems={subTotalCount}
                />
              </div>
              {createSettingTable(settingItem)}
            </div>
          </Modal>
        </>
      )}
    </div>
  )
}

export default StaffGroupSetting;